const SUBSCRIBER_LIST_PREFS_KEY = 'lccto-subscribers-list-prefs-v2';

const SORTABLE_COLUMNS = new Set([
  'email',
  'name',
  'email_status',
  'funnel_status',
  'source',
  'last_contacted_at',
  'created_at',
  'profile',
]);

export type SubscriberListSortConfig = {
  column: string;
  direction: 'asc' | 'desc';
};

/** Default list sort: newest subscribers first. */
export const DEFAULT_SUBSCRIBER_LIST_SORT: SubscriberListSortConfig = {
  column: 'created_at',
  direction: 'desc',
};

export type SubscriberListPrefs = {
  searchQuery: string;
  selectedChannelFilters: string[];
  emailStatusFilter: string;
  funnelStatusFilter: string;
  sourceFilter: string;
  filtersPanelCollapsed: boolean;
  fieldsPanelCollapsed: boolean;
  sortConfig: SubscriberListSortConfig | null;
};

export const DEFAULT_SUBSCRIBER_LIST_PREFS: SubscriberListPrefs = {
  searchQuery: '',
  selectedChannelFilters: [],
  emailStatusFilter: '',
  funnelStatusFilter: '',
  sourceFilter: '',
  filtersPanelCollapsed: true,
  fieldsPanelCollapsed: true,
  sortConfig: DEFAULT_SUBSCRIBER_LIST_SORT,
};

function parseStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function parseSortConfig(raw: unknown): SubscriberListSortConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.column !== 'string' || !SORTABLE_COLUMNS.has(o.column)) return null;
  if (o.direction !== 'asc' && o.direction !== 'desc') return null;
  return { column: o.column, direction: o.direction };
}

export function loadSubscriberListPrefs(): SubscriberListPrefs {
  try {
    const raw = localStorage.getItem(SUBSCRIBER_LIST_PREFS_KEY);
    if (!raw) return { ...DEFAULT_SUBSCRIBER_LIST_PREFS };
    const parsed = JSON.parse(raw) as Partial<SubscriberListPrefs>;
    return {
      searchQuery: typeof parsed.searchQuery === 'string' ? parsed.searchQuery : '',
      selectedChannelFilters: parseStringArray(parsed.selectedChannelFilters),
      emailStatusFilter: typeof parsed.emailStatusFilter === 'string' ? parsed.emailStatusFilter : '',
      funnelStatusFilter: typeof parsed.funnelStatusFilter === 'string' ? parsed.funnelStatusFilter : '',
      sourceFilter: typeof parsed.sourceFilter === 'string' ? parsed.sourceFilter : '',
      filtersPanelCollapsed:
        typeof parsed.filtersPanelCollapsed === 'boolean'
          ? parsed.filtersPanelCollapsed
          : DEFAULT_SUBSCRIBER_LIST_PREFS.filtersPanelCollapsed,
      fieldsPanelCollapsed:
        typeof parsed.fieldsPanelCollapsed === 'boolean'
          ? parsed.fieldsPanelCollapsed
          : DEFAULT_SUBSCRIBER_LIST_PREFS.fieldsPanelCollapsed,
      sortConfig: parseSortConfig(parsed.sortConfig) ?? DEFAULT_SUBSCRIBER_LIST_SORT,
    };
  } catch {
    return { ...DEFAULT_SUBSCRIBER_LIST_PREFS };
  }
}

export function persistSubscriberListPrefs(prefs: SubscriberListPrefs): void {
  try {
    localStorage.setItem(SUBSCRIBER_LIST_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / private mode */
  }
}

export function isSortableSubscriberListColumn(column: string): boolean {
  return SORTABLE_COLUMNS.has(column);
}
